// src/scenes/GuestMenuSceneDOM.js - Global DOM implementation (working architecture)
import BaseMenuScene from "./BaseMenuScene.js";
import PPTHeroSceneManager from "./ppt-hero/PPTHeroSceneManager.js";
import { showAuthOverlay } from "../ui/authUI.js";
import { hasSpecialAccess } from "../services/specialAccessService.js";
import sceneManager from "../utils/SceneManager.js";
// REMOVED: ResponsiveDesign import - now handled by UnifiedMobileSystem

export default class GuestMenuSceneDOM extends BaseMenuScene {
    constructor() {
        super({ key: "GuestMenuSceneDOM" });
        this.overlayId = "guest-menu-dom-overlay";
        this.specialAccessChecked = false;
        this.sceneReady = false; // Prevent premature interactions
        this.actionInProgress = false; // Prevent multiple rapid clicks
    }

    create() {
        console.log("GuestMenuSceneDOM: create() called - Using global DOM with proper cleanup");
        super.create();
        
        // CRITICAL FIX: Reset scene readiness flag on every create (important for ribbon navigation return)
        this.sceneReady = false;
        console.log("GuestMenuSceneDOM: Scene readiness flag reset for fresh initialization");
        
        // Set ribbon visibility and hide game container properly
        this.setRibbonVisibility(true); // Guests can see ribbon
        this.hideGameContainer();
        
        // Create beautiful DOM guest interface using working architecture
        this.createGlobalDOMInterface();
        
        // UNIFIED: Mobile enhancements handled by UnifiedMobileSystem
        console.log('📱 Mobile enhancements handled by UnifiedMobileSystem');
        
        console.log("GuestMenuSceneDOM: Global DOM interface created with mobile optimization");
    }

    // REMOVED: Conflicting enterprise mobile enhancement system
    // All mobile functionality is now handled by UnifiedMobileSystem

    enhanceForMobile() {
        // REMOVED: Conflicting mobile enhancement system
        // Mobile enhancements are now handled by UnifiedMobileSystem
        console.log('📱 Mobile enhancements delegated to UnifiedMobileSystem');
    }

    // REMOVED: Duplicate mobile scrolling code - UnifiedMobileSystem handles this

    hideGameContainer() {
        const gameContainer = document.getElementById("game-container");
        if (gameContainer) {
            this.originalGameContainerStyles = {
                display: gameContainer.style.display || 'block',
                visibility: gameContainer.style.visibility || 'visible'
            };
            gameContainer.style.display = 'none';
            gameContainer.style.visibility = 'hidden';
        }
    }

    showGameContainer() {
        const gameContainer = document.getElementById("game-container");
        if (gameContainer) {
            // CRITICAL: Restore PPT Hero expected positioning (from audit)
            gameContainer.style.visibility = 'visible';
            gameContainer.style.display = 'block';
            gameContainer.style.top = '50px';
            gameContainer.style.height = 'calc(100% - 50px)';
            gameContainer.style.opacity = '1';
            gameContainer.style.filter = 'none';
            
            // Also ensure top ribbon is visible (PPT Hero dependency)
            const topRibbon = document.getElementById("top-ribbon");
            if (topRibbon) {
                topRibbon.style.display = 'flex';
            }
            
            // CRITICAL: Canvas styling for Rex UI (from PPT Hero requirements)
            const canvas = document.querySelector('canvas');
            if (canvas) {
                canvas.style.opacity = '1';
                canvas.style.filter = 'none';
                canvas.style.touchAction = "pan-y"; // FIXED: Allow mobile scrolling
            }
            
            // CRITICAL: Call CanvasFixer (PPT Hero dependency)
            try {
                if (window.CanvasFixer && typeof window.CanvasFixer.fixAndFocusCanvas === 'function') {
                    window.CanvasFixer.fixAndFocusCanvas();
                }
            } catch (e) {
                console.warn("Error calling CanvasFixer:", e);
            }
            
            // CRITICAL: Clean up any DOM-introduced CSS pollution  
            this.cleanupCSSPollution();
            
            // CRITICAL: Force scene to top for Rex UI visibility
            if (window.gameInstance) {
                const currentScene = window.gameInstance.scene.getScene('PPTHeroGenericScene') || 
                                   window.gameInstance.scene.getScene('PPTHeroBainScene');
                if (currentScene && currentScene.scene && typeof currentScene.scene.bringToTop === 'function') {
                    currentScene.scene.bringToTop();
                    console.log('GuestMenuSceneDOM: Brought PPT Hero scene to top for Rex UI visibility');
                }
            }
            
            console.log('GuestMenuSceneDOM: Complete Rex UI environment restored with CSS cleanup');
        }
    }

    createGlobalDOMInterface() {
        // Remove existing overlay
        this.removeExistingOverlay();

        // Check for special access status
        const specialAccess = hasSpecialAccess();

        // Create main overlay using working global DOM architecture
        const overlay = document.createElement('div');
        overlay.id = this.overlayId;
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            z-index: 10000;
            overflow-y: auto;
            font-family: 'Segoe UI', sans-serif;
            color: white;
        `;

        overlay.innerHTML = `
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 24px 48px; background: rgba(255, 255, 255, 0.02); border-bottom: 1px solid rgba(255, 255, 255, 0.1);">
              <div style="font-size: 28px; font-weight: 800; color: #00d4ff;">DeckOps.ai</div>
              <div style="display: flex; gap: 16px;">
                <button data-action="sign-in" style="padding: 12px 24px; border: 2px solid #00d4ff; border-radius: 8px; background: transparent; color: #00d4ff; font-weight: 600; cursor: pointer;">Sign In</button>
                <button data-action="sign-up" style="padding: 12px 24px; border: none; border-radius: 8px; background: #00d4ff; color: white; font-weight: 600; cursor: pointer;">Get Full Access</button>
              </div>
            </div>

            <div style="text-align: center; padding: 60px 24px 40px 24px;">
              <h1 style="font-size: 42px; font-weight: 800; margin-bottom: 20px; color: white; line-height: 1.2;">Welcome ${specialAccess ? 'Premium Guest' : 'Guest'}</h1>
              <p style="font-size: 18px; color: rgba(255, 255, 255, 0.8); margin-bottom: 36px; line-height: 1.5; max-width: 90%; margin-left: auto; margin-right: auto;">Experience executive performance training with ${specialAccess ? 'premium access. Enjoy enhanced features' : 'limited access. Sign up for full analytics'} and competitive leaderboards.</p>
              
              <div class="hero-buttons" style="display: flex; flex-direction: row; gap: 24px; max-width: 600px; margin: 0 auto 60px auto; justify-content: center;">
                <button data-action="sign-up" style="padding: 18px 36px; border-radius: 12px; background: #00d4ff; color: white; border: none; cursor: pointer; font-size: 16px; font-weight: 600;">Unlock Full Platform</button>
                <button data-action="explore-games" style="padding: 18px 36px; border-radius: 12px; background: transparent; color: white; border: 2px solid white; cursor: pointer; font-size: 16px; font-weight: 600;">Try Available Games</button>
              </div>
            </div>

            <div style="padding: 0 24px 80px 24px;">
              <h2 style="text-align: center; font-size: 28px; margin-bottom: 32px; color: white;">Available Training Modules</h2>

              ${specialAccess ? `
                <div style="background: rgba(16, 185, 129, 0.1); border: 1px solid rgba(16, 185, 129, 0.2); border-radius: 12px; padding: 20px; margin-bottom: 32px; text-align: center;">
                  <h3 style="color: #10b981; font-size: 18px; font-weight: 600; margin-bottom: 8px;">✨ Premium Guest Access Active</h3>
                  <p style="color: rgba(255, 255, 255, 0.8); font-size: 14px;">You have temporary access to premium features. Sign up to make this permanent.</p>
                </div>
              ` : `
                <div style="background: rgba(168, 85, 247, 0.1); border: 1px solid rgba(168, 85, 247, 0.2); border-radius: 12px; padding: 20px; margin-bottom: 32px; text-align: center;">
                  <h3 style="color: #a855f7; font-size: 18px; font-weight: 600; margin-bottom: 8px;">🎯 Guest Access Available</h3>
                  <p style="color: rgba(255, 255, 255, 0.8); font-size: 14px;">Experience our training modules with limited access. Sign up for full analytics and progress tracking.</p>
                </div>
              `}

              <div class="games-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(350px, 1fr)); gap: 24px; max-width: 1200px; margin: 0 auto;">
                <div data-game="ppt-hero" class="game-card" style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 16px; padding: 28px; cursor: pointer;">
                  <div style="font-size: 48px; margin-bottom: 16px;">📊</div>
                  <h3 style="font-size: 24px; font-weight: 700; margin-bottom: 12px; color: white;">PPT Hero</h3>
                  <p style="font-size: 14px; color: rgba(255, 255, 255, 0.8); margin-bottom: 20px;">Master PowerPoint shortcuts and presentation workflows ${specialAccess ? 'with enhanced features' : 'in demo mode'}.</p>
                  <button style="width: 100%; padding: 14px; border: none; border-radius: 10px; background: #00d4ff; color: white; cursor: pointer; font-size: 16px; font-weight: 600;">Play Now</button>
                </div>

                <div data-game="philosophy-flow" class="game-card" style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 16px; padding: 28px; cursor: pointer;">
                  <div style="font-size: 48px; margin-bottom: 16px;">🧠</div>
                  <h3 style="font-size: 24px; font-weight: 700; margin-bottom: 12px; color: white;">Philosophy Flow</h3>
                  <p style="font-size: 14px; color: rgba(255, 255, 255, 0.8); margin-bottom: 20px;">Strategic thinking and framework application. Develop executive decision-making through philosophical reasoning challenges.</p>
                  <button style="width: 100%; padding: 14px; border: none; border-radius: 10px; background: #00d4ff; color: white; cursor: pointer; font-size: 16px; font-weight: 600;">Play Now</button>
                </div>

                <div data-game="slide-wars" style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 16px; padding: 28px; cursor: not-allowed; opacity: 0.6;">
                  <div style="font-size: 48px; margin-bottom: 16px;">⚔️</div>
                  <h3 style="font-size: 24px; font-weight: 700; margin-bottom: 12px; color: white;">Slide Wars</h3>
                  <p style="font-size: 14px; color: rgba(255, 255, 255, 0.8); margin-bottom: 20px;">Competitive presentation building under time pressure. Battle other executives in real-time slide creation challenges.</p>
                  <div style="background: rgba(255, 255, 255, 0.1); border-radius: 8px; padding: 12px; margin: 16px 0;">
                    <p style="color: #fbbf24; font-size: 13px; margin: 0; text-align: center;">🚧 Still in development - coming soon!</p>
                  </div>
                  <button style="width: 100%; padding: 14px; border: none; border-radius: 10px; background: #64748b; color: rgba(255, 255, 255, 0.5); cursor: not-allowed;" onclick="alert('Slide Wars is still in development - coming soon!')">Coming Soon</button>
                </div>

                <div data-game="shortcut-mastery" style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 16px; padding: 28px; cursor: not-allowed; opacity: 0.6;">
                  <div style="font-size: 48px; margin-bottom: 16px;">⚡</div>
                  <h3 style="font-size: 24px; font-weight: 700; margin-bottom: 12px; color: white;">Shortcut Mastery</h3>
                  <p style="font-size: 14px; color: rgba(255, 255, 255, 0.8); margin-bottom: 20px;">Master keyboard shortcuts across applications. Build muscle memory for professional efficiency.</p>
                  <div style="background: rgba(255, 255, 255, 0.1); border-radius: 8px; padding: 12px; margin: 16px 0;">
                    <p style="color: #fbbf24; font-size: 13px; margin: 0; text-align: center;">🚧 Still in development - coming soon!</p>
                  </div>
                  <button style="width: 100%; padding: 14px; border: none; border-radius: 10px; background: #64748b; color: rgba(255, 255, 255, 0.5); cursor: not-allowed;" onclick="alert('Shortcut Mastery is still in development - coming soon!')">Coming Soon</button>
                </div>

                <div data-game="classify-tech" style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 16px; padding: 28px; cursor: not-allowed; opacity: 0.6;">
                  <div style="font-size: 48px; margin-bottom: 16px;">🔬</div>
                  <h3 style="font-size: 24px; font-weight: 700; margin-bottom: 12px; color: white;">Classify Tech</h3>
                  <p style="font-size: 14px; color: rgba(255, 255, 255, 0.8); margin-bottom: 20px;">Technology categorization and emerging tech identification. Build expertise in AI, cloud platforms, and innovation assessment.</p>
                  <div style="background: rgba(255, 255, 255, 0.1); border-radius: 8px; padding: 12px; margin: 16px 0;">
                    <p style="color: #fbbf24; font-size: 13px; margin: 0; text-align: center;">🚧 Still in development - coming soon!</p>
                  </div>
                  <button style="width: 100%; padding: 14px; border: none; border-radius: 10px; background: #64748b; color: rgba(255, 255, 255, 0.5); cursor: not-allowed;" onclick="alert('Classify Tech is still in development - coming soon!')">Coming Soon</button>
                </div>
              </div>

              <div style="text-align: center; margin-top: 64px; padding: 32px; background: rgba(255, 255, 255, 0.02); border-radius: 16px; border: 1px solid rgba(255, 255, 255, 0.1);">
                <h3 style="font-size: 24px; font-weight: 600; margin-bottom: 16px; color: white;">Ready for Executive-Level Access?</h3>
                <p style="font-size: 16px; color: rgba(255, 255, 255, 0.8); margin-bottom: 24px;">Unlock analytics, firm-specific content, competitive leaderboards, and advanced training modules</p>
                <button data-action="get-full-access" style="padding: 20px 40px; border: none; border-radius: 12px; background: #00d4ff; color: white; font-size: 18px; font-weight: 600; cursor: pointer;">Get Full Access</button>
              </div>
            </div>
        `;

        document.body.appendChild(overlay);
        
        // Add responsive CSS for proper desktop/mobile adaptation
        this.addResponsiveStyles();

        // UNIFIED: Use proper UnifiedMobileSystem (fix the method binding issue)
        const system = window.serviceLocator?.get('UnifiedMobileSystem');
        if (system && system.fixViewportAndCSS) {
            system.fixViewportAndCSS();
            console.log('✅ UnifiedMobileSystem applied mobile fixes');
        }

        // CRITICAL FIX: Delay event listeners to prevent auto-clicking during creation
        setTimeout(() => {
            this.addGlobalDOMEventListeners();
            this.sceneReady = true;
            console.log("✅ GuestMenuSceneDOM: Event listeners activated - scene ready for user interaction");
            
            // Enable input management for professional experience
            if (this.input && typeof this.input.enabled !== 'undefined') {
                this.input.enabled = true;
                console.log("✅ GuestMenuSceneDOM: Phaser input enabled");
            }
            
            // Signal to UIController that scene is ready
            if (window.UIController && typeof window.UIController.setActiveScene === 'function') {
                window.UIController.setActiveScene(this);
                console.log("✅ GuestMenuSceneDOM: UIController scene reference updated");
            }
            
        }, 1000); // Sufficient delay to prevent auto-clicking
    }

    addGlobalDOMEventListeners() {
        // Add click listeners using standard DOM events
        const actionButtons = document.querySelectorAll('[data-action]');
        actionButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                const action = e.currentTarget.dataset.action;
                this.handleAction(action);
            });
        });

        const gameCards = document.querySelectorAll('[data-game]');
        gameCards.forEach(card => {
            const gameName = card.dataset.game;
            const isComingSoon = ['slide-wars', 'shortcut-mastery', 'classify-tech'].includes(gameName);
            
            console.log(`🔗 Guest: Binding click to game card: ${gameName} (Coming Soon: ${isComingSoon})`);
            
            if (isComingSoon) {
                // For coming soon games, add a disabled click handler that shows a message
                card.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('🚫 Guest: Coming soon game clicked:', gameName);
                    
                    const gameDisplayNames = {
                        'slide-wars': 'Slide Wars',
                        'shortcut-mastery': 'Shortcut Mastery',
                        'classify-tech': 'Classify Tech'
                    };
                    
                    alert(`${gameDisplayNames[gameName]} is still in development - coming soon!`);
                });
                
                // Also disable any buttons within the card
                const buttons = card.querySelectorAll('button');
                buttons.forEach(button => {
                    button.disabled = true;
                    button.style.pointerEvents = 'auto'; // FIXED: Keep buttons clickable
                });
            } else {
                // For active games, use the normal click handler
                card.addEventListener('click', (e) => {
                    const game = e.currentTarget.dataset.game;
                    this.handleGameAction(game);
                });
            }
        });
        
        console.log("GuestMenuSceneDOM: Global DOM event listeners added");
    }

    addResponsiveStyles() {
        // ENTERPRISE REFACTOR: Use MobileDetectionService for proper device detection
        const isMobile = window.MobileDetectionService?.isMobile() || window.innerWidth <= 768;
        
        const style = document.createElement('style');
        style.id = 'guest-menu-responsive-styles';
        
        if (isMobile) {
            style.textContent = `
                #${this.overlayId} .hero-buttons {
                    flex-direction: column !important;
                    max-width: 320px !important;
                    gap: 16px !important;
                }
                #${this.overlayId} .hero-buttons button {
                    padding: 16px 24px !important;
                    min-height: 48px !important;
                    touch-action: manipulation !important;
                }
                #${this.overlayId} .games-grid {
                    grid-template-columns: 1fr !important;
                    max-width: 400px !important;
                    gap: 20px !important;
                }
                #${this.overlayId} .game-card button {
                    min-height: 48px !important;
                    touch-action: manipulation !important;
                    padding: 16px !important;
                }
            `;
        } else {
            style.textContent = `
                #${this.overlayId} .hero-buttons {
                    flex-direction: row !important;
                    max-width: 600px !important;
                    gap: 24px !important;
                }
                #${this.overlayId} .hero-buttons button {
                    padding: 18px 36px !important;
                    min-height: auto !important;
                    touch-action: auto !important;
                }
                #${this.overlayId} .games-grid {
                    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr)) !important;
                    max-width: 1200px !important;
                    gap: 24px !important;
                }
                #${this.overlayId} .game-card button {
                    min-height: auto !important;
                    touch-action: auto !important;
                    padding: 14px !important;
                }
            `;
        }
        
        const existingStyle = document.getElementById('guest-menu-responsive-styles');
        if (existingStyle) existingStyle.remove();
        
        document.head.appendChild(style);
        console.log(`📱 Applied ${isMobile ? 'mobile' : 'desktop'} styles for GuestMenuSceneDOM`);
    }

    handleAction(action) {
        // CRITICAL: Prevent interactions during scene creation
        if (!this.sceneReady) {
            console.log('🚫 GuestMenuSceneDOM: Ignoring premature action during creation:', action);
            console.log('🔍 DEBUG: sceneReady flag:', this.sceneReady);
            console.log('🔍 DEBUG: Scene state:', {
                sceneReady: this.sceneReady,
                inputEnabled: this.input?.enabled,
                isTransitioning: this.isTransitioning || false
            });
            return;
        }
        
        console.log(`🔍 GuestMenuSceneDOM: Handling action: ${action}`);
        console.log(`🔍 Available services:`, {
            enterpriseAuthService: !!window.enterpriseAuthService,
            authDialogs: !!window.authDialogs,
            authUI: !!window.authUI
        });
        
        switch (action) {
            case 'sign-in':
                console.log('🔍 GuestMenuSceneDOM: Calling showSignIn()');
                this.showSignIn();
                break;
            case 'sign-up':
            case 'get-full-access':
                console.log('🔍 GuestMenuSceneDOM: Calling showSignUp()');
                this.showSignUp();
                break;
            case 'explore-games':
                // Could scroll to games section
                break;
            default:
                console.log('Unknown action:', action);
        }
    }

    handleGameAction(game) {
        console.log(`🔍 GuestMenuSceneDOM: Game action requested - ${game}`, {
            sceneReady: this.sceneReady,
            inputEnabled: this.input?.enabled,
            isTransitioning: this.isTransitioning || false,
            sceneManagerTransitioning: window.sceneManager?.isTransitioning || false,
            sceneManagerCooldown: window.sceneManager?.transitionCooldown || false
        });
        
        // CRITICAL: Prevent interactions during scene creation
        if (!this.sceneReady) {
            console.warn('🚨 GuestMenuSceneDOM: BLOCKING game action - scene not ready:', game);
            console.warn('🚨 This suggests the scene was not properly initialized after ribbon navigation');
            return;
        }
        
        console.log(`✅ GuestMenuSceneDOM: Processing game action for: ${game}`);
        const specialAccess = hasSpecialAccess();

        switch (game) {
            case 'ppt-hero':
                this.startPPTHero();
                break;
            case 'slide-wars':
                if (specialAccess) {
                    this.startSlideWars();
                } else {
                    this.showSignUpPrompt('Slide Wars', 'Competitive presentation battles require full account access.');
                }
                break;
            case 'philosophy-flow':
                this.startPhilosophyFlow();
                break;
            case 'classify-tech':
                // Classify Tech remains disabled - under construction
                console.log('Classify Tech is disabled - under construction');
                this.showMessage('Classify Tech is still in development - coming soon!', 3000, false);
                break;
            default:
                console.log('Unknown game:', game);
        }
    }

    // Action methods
    async showSignIn() {
        console.log("GuestMenuSceneDOM: Navigating to full-page sign-in experience");
        try {
            // Use proper scene transition to full-page sign-in
            this.startScene('SignInSceneDOM');
        } catch (e) {
            console.error("Error navigating to sign-in scene:", e);
            this.showMessage("Error loading sign-in page", 2000, true);
        }
    }

    async showSignUp() {
        console.log("GuestMenuSceneDOM: Navigating to full-page sign-up experience");
        try {
            // Use proper scene transition to full-page sign-up
            this.startScene('SignUpSceneDOM');
        } catch (e) {
            console.error("Error navigating to sign-up scene:", e);
            this.showMessage("Error loading sign-up page", 2000, true);
        }
    }

    showSignUpPrompt(gameName, description) {
        const message = `${gameName}: ${description}\n\nSign up for full access to analytics, leaderboards, and premium features.`;
        
        if (confirm(message + '\n\nWould you like to sign up now?')) {
            this.showSignUp();
        }
    }

    // Game navigation methods - LET PPT Hero handle its own environment  
    async startPPTHero() {
        console.log("GuestMenuSceneDOM: Starting PPT Hero (Generic)");
        
        // CRITICAL: ONLY remove DOM overlay - let PPT Hero handle container setup
        this.removeExistingOverlay();
        
        try {
            // Use EXACT same method as working old GuestMenuScene
            await PPTHeroSceneManager.startPPTHero(this, 'generic'); // Guests always get generic
        } catch (error) {
            console.error('Error loading PPT Hero:', error);
            this.showMessage('Error loading PPT Hero. Please try again.', 3000, true);
        }
    }

    startSlideWars() {
        console.log("GuestMenuSceneDOM: Starting Slide Wars");
        // CRITICAL: ONLY remove DOM overlay - let game handle container setup
        this.removeExistingOverlay();
        sceneManager.switchToScene("GameScene");
    }

    startPhilosophyFlow() {
        console.log('🧠 GuestMenuSceneDOM: Starting Philosophy Flow for guests...');
        
        // Philosophy Flow is available for guests
        try {
            // CRITICAL: ONLY remove DOM overlay - let game handle container setup
            this.removeExistingOverlay();
            sceneManager.switchToScene("PhilosophyFlowScene");
            console.log('✅ Philosophy Flow started for guest user');
            
        } catch (error) {
            console.error('❌ Error starting Philosophy Flow:', error);
            this.showMessage('Unable to start Philosophy Flow. Please try again.', 3000, true);
        }
    }

    // Classify Tech method removed - game remains disabled under construction

    showMessage(text, duration = 2000, isError = false) {
        if (super.showMessage) {
            super.showMessage(text, duration, isError);
        } else {
            console.log(`GuestMenu Message: ${text}`);
        }
    }

    removeExistingOverlay() {
        const existing = document.getElementById(this.overlayId);
        if (existing) {
            existing.remove();
        }
    }

    cleanupCSSPollution() {
        // CRITICAL: Remove ALL DOM-introduced styles that might interfere with Rex UI
        const styleElements = document.querySelectorAll('style');
        let removedCount = 0;
        
        styleElements.forEach(style => {
            const content = style.textContent || '';
            // Remove styles that might interfere with Phaser/Rex UI
            if (content.includes('@keyframes') || 
                content.includes('position: fixed') || 
                content.includes('z-index:') ||
                content.includes('.header') ||
                content.includes('.guest-notice') ||
                content.includes('.games-grid')) {
                style.remove();
                removedCount++;
            }
        });
        
        console.log(`GuestMenuSceneDOM: Cleaned up ${removedCount} DOM style elements that could interfere with Rex UI`);
        
        // Force canvas and container style reset
        const canvas = document.querySelector('canvas');
        if (canvas) {
            canvas.style.position = '';
            canvas.style.zIndex = '';
            canvas.style.transform = '';
        }
        
        const gameContainer = document.getElementById("game-container");
        if (gameContainer) {
            gameContainer.style.transform = '';
            gameContainer.style.zIndex = '';
            gameContainer.style.backgroundColor = 'transparent';
            gameContainer.style.background = 'none';
        }
    }

    shutdown() {
        console.log("GuestMenuSceneDOM shutting down.");
        
        // CRITICAL FIX: Reset scene state flags for proper re-initialization
        this.sceneReady = false;
        this.specialAccessChecked = false;
        console.log("GuestMenuSceneDOM: State flags reset during shutdown");
        
        this.removeExistingOverlay();
        // Let games handle their own container environment
    }

    destroy() {
        this.removeExistingOverlay();
        // Let games handle their own container environment
        super.destroy();
    }
}
